"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";

import type { IDoctorAvailability, ITimeSlot } from "@/app/interfaces/doctor.interface";
import type IHeaderSchema from "@/app/interfaces/header";

interface TimeSlotSelectorProps {
  locale: string;
  headers: IHeaderSchema;
  doctorId: string;
  doctorName: string;
  onAppointmentComplete: (doctorId: string, timeSlotId: string, date: string, time: string) => void;
}

const t = (key: string, headers: IHeaderSchema): string => {
  return (headers as unknown as Record<string, string>)[key] || key;
};

export default function TimeSlotSelector({
  locale,
  headers,
  doctorId,
  doctorName,
  onAppointmentComplete,
}: TimeSlotSelectorProps) {
  const [availability, setAvailability] = useState<IDoctorAvailability | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<ITimeSlot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/doctors/${doctorId}/availability`, {
          params: { locale },
        });
        setAvailability(response.data);
        if (response.data?.availableSlots && response.data.availableSlots.length > 0) {
          const availableSlot = response.data.availableSlots.find((slot: ITimeSlot) => slot.isAvailable);
          if (availableSlot) {
            setSelectedTimeSlot(availableSlot);
          }
        }
      } catch (err) {
        setError(t("noTimeSlotsAvailable", headers));
        console.error("Error fetching availability:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [doctorId, locale, headers]);

  const groupedByDate = availability?.availableSlots.reduce(
    (acc, slot) => {
      if (!acc[slot.date]) {
        acc[slot.date] = [];
      }
      acc[slot.date].push(slot);
      return acc;
    },
    {} as Record<string, ITimeSlot[]>,
  );

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString(locale === "pl" ? "pl-PL" : "en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t("loading", headers)}</p>
      </div>
    );
  }

  if (error || !availability || availability.availableSlots.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error || t("noTimeSlotsAvailable", headers)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          {t("selectTime", headers)}
        </label>

        <div className="space-y-4">
          {groupedByDate &&
            Object.entries(groupedByDate).map(([date, slots]) => (
              <div key={date} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">{formatDate(date)}</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4">
                  {slots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedTimeSlot(slot)}
                      disabled={!slot.isAvailable}
                      className={`py-3 px-2 rounded-lg font-medium transition-all flex items-center justify-center gap-1 ${
                        selectedTimeSlot?.id === slot.id
                          ? "bg-blue-600 text-white border-2 border-blue-700"
                          : slot.isAvailable
                            ? "bg-blue-50 text-blue-700 border-2 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                            : "bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-not-allowed"
                      }`}
                    >
                      <ClockIcon className="w-4 h-4" />
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Reserve Button */}
      <button
        onClick={() => {
          if (selectedTimeSlot) {
            onAppointmentComplete(doctorId, selectedTimeSlot.id, selectedTimeSlot.date, selectedTimeSlot.time);
          }
        }}
        disabled={!selectedTimeSlot}
        className={`w-full py-4 rounded-lg font-semibold text-lg transition-all ${
          selectedTimeSlot
            ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {t("reserve", headers)}
      </button>
    </div>
  );
}
